import { Box, Chip, MenuItem, Select } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView, PaloStatusStep } from '@synergy/components-palo';
import { tokens } from '@synergy/theme-palo';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SEARCH_CONSOLE_DAYS } from './palo-search-console';
import { SearchConsoleTab } from '../models/search-console-tab';
import { SearchWorklistEntry, SearchWorklistFinding, SearchWorklistStatus } from '../models/search-quality';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

/** The 20 most rewarding terms, per acceptance criterion 3 of #2173. */
const WORKLIST_LIMIT = 20;

/**
 * The severities behind the score, as the backend applies them. They are repeated
 * here only to be shown: the order of the list has to be arguable from the screen.
 */
const SEVERITY: Record<SearchWorklistFinding, number> = {
    no_results: 1.0,
    first_hit_mismatch: 0.7,
    result_flood: 0.5,
    no_click: 0.3,
};

/** A number or GTIN that finds nothing is usually an article the catalogue does not carry. */
const NUMBER_NO_RESULTS_SEVERITY = 0.2;

const findingStep: Record<SearchWorklistFinding, PaloStatusStep> = {
    no_results: PaloStatusStep.Problem,
    first_hit_mismatch: PaloStatusStep.WaitingForMe,
    result_flood: PaloStatusStep.WaitingForMe,
    no_click: PaloStatusStep.Draft,
};

const STATUSES: SearchWorklistStatus[] = ['open', 'in_review', 'done'];

/**
 * Ansicht 2 — Arbeitsliste. Not every weak search, but the ones worth the effort:
 * ordered by how many different people ran into it times how bad it was. Every row
 * leads into the explanation of that very search.
 */
export const SearchWorklistView = () => {
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const [entries, setEntries] = useState<SearchWorklistEntry[]>([]);
    const [requesting, setRequesting] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setRequesting(true);

        void dispatch(async (_dispatch, _getState, { api }) => {
            const result = await api.searchQuality.getWorklist(SEARCH_CONSOLE_DAYS, WORKLIST_LIMIT);
            if (cancelled) {
                return;
            }
            setEntries(result.success ? result.result.entries : []);
            setRequesting(false);
        });

        return () => {
            cancelled = true;
        };
    }, [dispatch]);

    const changeStatus = (entry: SearchWorklistEntry, status: SearchWorklistStatus) => {
        const before = entries;
        setEntries(current => current.map(item => (item.term === entry.term ? { ...item, status } : item)));

        void dispatch(async (_dispatch, _getState, { api }) => {
            const result = await api.searchQuality.setWorklistStatus(entry.term, status);
            if (!result.success) {
                setEntries(before);
            }
        });
    };

    const openExplanation = (entry: SearchWorklistEntry) => {
        navigate({ pathname: `../${SearchConsoleTab.Explain}`, search: `?term=${encodeURIComponent(entry.term)}` });
    };

    const columns: PaloColumn<SearchWorklistEntry>[] = [
        {
            key: 'term',
            type: 'name',
            head: <FormattedMessage id="app.searchConsole.column.term" />,
            cell: entry => <Box sx={{ fontWeight: 550 }}>{entry.term}</Box>,
        },
        {
            key: 'queryType',
            type: 'classification',
            head: <FormattedMessage id="app.searchConsole.column.queryType" />,
            variant: 'muted',
            cell: entry => <FormattedMessage id={`app.searchConsole.queryType.${entry.queryType}`} />,
        },
        {
            key: 'users',
            head: <FormattedMessage id="app.searchConsole.column.users" />,
            align: 'right',
            variant: 'amount',
            cell: entry => String(entry.users),
        },
        {
            key: 'searches',
            head: <FormattedMessage id="app.searchConsole.column.searches" />,
            align: 'right',
            variant: 'amount',
            cell: entry => String(entry.searches),
        },
        {
            key: 'medianResultCount',
            head: <FormattedMessage id="app.searchConsole.column.medianResults" />,
            align: 'right',
            variant: 'amount',
            cell: entry => entry.medianResultCount.toLocaleString('de-AT'),
        },
        {
            key: 'finding',
            head: <FormattedMessage id="app.searchConsole.column.finding" />,
            cell: entry => (
                <Chip
                    size="small"
                    color={findingStep[entry.finding]}
                    label={<FormattedMessage id={`app.searchConsole.finding.${entry.finding}`} />}
                />
            ),
        },
        {
            key: 'score',
            head: <FormattedMessage id="app.searchConsole.column.score" />,
            align: 'right',
            variant: 'amount',
            cell: entry => entry.score.toFixed(1).replace('.', ','),
        },
        {
            key: 'status',
            type: 'status',
            head: <FormattedMessage id="app.searchConsole.column.status" />,
            cell: entry => (
                <Select
                    size="small"
                    value={entry.status}
                    onClick={event => event.stopPropagation()}
                    onChange={event => changeStatus(entry, event.target.value as SearchWorklistStatus)}
                    sx={{ fontSize: '12px', minWidth: 130 }}
                >
                    {STATUSES.map(status => (
                        <MenuItem key={status} value={status} sx={{ fontSize: '12px' }}>
                            <FormattedMessage id={`app.searchConsole.status.${status}`} />
                        </MenuItem>
                    ))}
                </Select>
            ),
        },
    ];

    const weight = (value: number) => `× ${value.toFixed(1).replace('.', ',')}`;

    const legendRow = (finding: SearchWorklistFinding, severity: number, descriptionId: string, outlined = false) => (
        <Box sx={{ display: 'grid', gridTemplateColumns: '150px 44px 1fr', gap: '10px', alignItems: 'center' }}>
            <Chip
                size="small"
                color={findingStep[finding]}
                variant={outlined ? 'outlined' : 'filled'}
                label={<FormattedMessage id={`app.searchConsole.finding.${finding}`} />}
                sx={{ justifySelf: 'start' }}
            />
            <Box sx={{ fontFamily: tokens.font.mono, fontSize: '12px', color: tokens.color.ink, textAlign: 'right' }}>
                {weight(severity)}
            </Box>
            <Box sx={{ fontSize: '12px', color: tokens.color.ink2 }}>
                <FormattedMessage id={descriptionId} />
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Box sx={{ fontSize: '12px', color: tokens.color.ink2 }}>
                <FormattedMessage id="app.searchConsole.worklist.intro" values={{ days: SEARCH_CONSOLE_DAYS }} />
            </Box>
            <PaloListView
                embedded={true}
                columns={columns}
                items={entries}
                getRowId={entry => entry.term}
                requesting={requesting}
                emptyLabel={<FormattedMessage id="app.searchConsole.worklist.empty" />}
                onRowClick={openExplanation}
                isRowMuted={entry => entry.status === 'done'}
            />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    padding: '12px 14px',
                    border: `1px solid ${tokens.color.line}`,
                    borderRadius: '8px',
                    backgroundColor: tokens.color.surface,
                    maxWidth: 720,
                }}
            >
                <Box sx={{ fontSize: '12px', fontWeight: 640, color: tokens.color.ink }}>
                    <FormattedMessage id="app.searchConsole.worklist.legend.title" />
                </Box>
                {legendRow('no_results', SEVERITY.no_results, 'app.searchConsole.worklist.legend.noResults')}
                {legendRow(
                    'no_results',
                    NUMBER_NO_RESULTS_SEVERITY,
                    'app.searchConsole.worklist.legend.numberNoResults',
                    true
                )}
                {legendRow(
                    'first_hit_mismatch',
                    SEVERITY.first_hit_mismatch,
                    'app.searchConsole.worklist.legend.firstHitMismatch'
                )}
                {legendRow('result_flood', SEVERITY.result_flood, 'app.searchConsole.worklist.legend.resultFlood')}
                {legendRow('no_click', SEVERITY.no_click, 'app.searchConsole.worklist.legend.noClick')}
                <Box sx={{ fontSize: '11px', color: tokens.color.ink3 }}>
                    <FormattedMessage id="app.searchConsole.worklist.legend.tieBreak" />
                </Box>
            </Box>
        </Box>
    );
};
